import { useState, useEffect } from 'react'
import styles from './EditClientModal.module.css'
import apiClient from '../../utils/api'
import useEscapeKey from '../../hooks/useEscapeKey'

export default function EditClientModal({ 
    isOpen, 
    onClose, 
    onSave,
    client,
    apiToken = null
}) {
    const [formData, setFormData] = useState({
        name: '',
        currency: 'USD',
        date_format: 'MM/DD/YYYY',
        status: 'active'
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [fieldErrors, setFieldErrors] = useState({})

    // Add escape key functionality
    useEscapeKey(isOpen, onClose, !loading)

    // Initialize form data when modal opens or client changes
    useEffect(() => {
        if (isOpen && client) {
            setFormData({
                name: client.name || '',
                currency: client.currency || 'USD',
                date_format: client.date_format || 'MM/DD/YYYY',
                status: client.status || 'active'
            })
            setError('')
            setFieldErrors({})
        }
    }, [isOpen, client])

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
        
        // Clear field error when user starts typing
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: ''
            }))
        }
    }

    const validateForm = () => {
        const errors = {}
        
        if (!formData.name.trim()) {
            errors.name = 'Client name is required'
        }
        
        if (!formData.currency.trim()) {
            errors.currency = 'Currency is required'
        }
        
        setFieldErrors(errors)
        return Object.keys(errors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) {
            return
        }

        if (!client) {
            setError('Client information is missing')
            return
        }

        setLoading(true)
        setError('')

        try {
            if (!apiToken) {
                throw new Error('Authentication token is required')
            }

            apiClient.setAuthToken(apiToken)
            
            const updateData = {
                name: formData.name.trim(),
                currency: formData.currency.trim(),
                date_format: formData.date_format,
                status: formData.status
            }

            const response = await apiClient.updateClient(client.id, updateData)
            
            // Call onSave callback with updated client data
            onSave(response.client || { ...client, ...updateData })
            
            // Close modal
            handleClose()
            
        } catch (err) {
            console.error('Failed to update client:', err)
            setError(err.message || 'Failed to update client. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        if (!loading) {
            setFormData({
                name: '',
                currency: 'USD',
                date_format: 'MM/DD/YYYY',
                status: 'active'
            })
            setError('')
            setFieldErrors({})
            onClose()
        }
    }

    const getCurrencyOptions = () => [
        { value: 'USD', label: 'USD - US Dollar' },
        { value: 'EUR', label: 'EUR - Euro' },
        { value: 'GBP', label: 'GBP - British Pound' },
        { value: 'INR', label: 'INR - Indian Rupee' },
        { value: 'CAD', label: 'CAD - Canadian Dollar' },
        { value: 'AUD', label: 'AUD - Australian Dollar' },
        { value: 'JPY', label: 'JPY - Japanese Yen' },
        { value: 'CHF', label: 'CHF - Swiss Franc' },
        { value: 'CNY', label: 'CNY - Chinese Yuan' }
    ]

    if (!isOpen) return null

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Edit Client</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={handleClose}
                        disabled={loading}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className={styles.modalBody}>
                        {client && (
                            <div className={styles.clientInfo}>
                                <span className={styles.clientLabel}>Editing:</span>
                                <span className={styles.clientName}>{client.name}</span>
                            </div>
                        )}

                        {error && (
                            <div className={styles.errorMessage}>
                                {error}
                            </div>
                        )}

                        <div className={styles.formSection}>
                            <div className={styles.formGroup}>
                                <label htmlFor="name" className={styles.label}>
                                    Client Name <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className={`${styles.input} ${fieldErrors.name ? styles.inputError : ''}`}
                                    disabled={loading}
                                    placeholder="Enter client name"
                                />
                                {fieldErrors.name && (
                                    <div className={styles.fieldError}>{fieldErrors.name}</div>
                                )}
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="currency" className={styles.label}>
                                    Currency <span className={styles.required}>*</span>
                                </label>
                                <select
                                    id="currency"
                                    name="currency"
                                    value={formData.currency}
                                    onChange={handleInputChange}
                                    className={`${styles.select} ${fieldErrors.currency ? styles.inputError : ''}`}
                                    disabled={loading}
                                >
                                    {getCurrencyOptions().map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {fieldErrors.currency && (
                                    <div className={styles.fieldError}>{fieldErrors.currency}</div>
                                )}
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="date_format" className={styles.label}>
                                    Date Format <span className={styles.required}>*</span>
                                </label>
                                <select
                                    id="date_format"
                                    name="date_format"
                                    value={formData.date_format}
                                    onChange={handleInputChange}
                                    className={`${styles.select} ${fieldErrors.date_format ? styles.inputError : ''}`}
                                    disabled={loading}
                                >
                                    <option value="MM/DD/YYYY">MM/DD/YYYY (US Format)</option>
                                    <option value="DD/MM/YYYY">DD/MM/YYYY (UK Format)</option>
                                    <option value="YYYY-MM-DD">YYYY-MM-DD (ISO Format)</option>
                                    <option value="DD.MM.YYYY">DD.MM.YYYY (German Format)</option>
                                    <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                                </select>
                                {fieldErrors.date_format && (
                                    <div className={styles.fieldError}>{fieldErrors.date_format}</div>
                                )}
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="status" className={styles.label}>
                                    Status <span className={styles.required}>*</span>
                                </label>
                                <select
                                    id="status"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    className={`${styles.select} ${fieldErrors.status ? styles.inputError : ''}`}
                                    disabled={loading}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="archived">Archived</option>
                                </select>
                                {fieldErrors.status && (
                                    <div className={styles.fieldError}>{fieldErrors.status}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <div className={styles.rightButtons}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.saveButton}
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}